import React, { useRef, useCallback } from 'react';
import { X, Link2, MessageCircle, Download, Mail } from 'lucide-react';
import buildWalkUrl from '../utils/walkUrl';
import categoryLabel from '../utils/categoryLabels';

const PURPLE = '#5B2D8E';

const ShareModal = ({ walk, isOpen, onClose }) => {
  const cardRef = useRef(null);

  const walkUrl = `${window.location.origin}${buildWalkUrl(walk || {})}`;
  const category = categoryLabel(walk?.categories?.[0]) || walk?.category || '';
  const neighborhood = walk?.region || 'Los Angeles';

  const shareText = `${walk?.title} — ${walk?.distance || ''} walk in ${neighborhood}`;
  const smsText = `Check out this walk on Wanderloop: ${walk?.title} (${walk?.distance || ''}, ${neighborhood}) → ${walkUrl}`;
  const emailSubject = `Check out this walk: ${walk?.title}`;
  const emailBody = `I found this walk on Wanderloop and thought you'd like it!\n\n${walk?.title}\n${[walk?.distance, walk?.duration, neighborhood].filter(Boolean).join(' · ')}\n\n${walkUrl}`;

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(walkUrl);
    alert('Link copied!');
  }, [walkUrl]);

  const downloadCard = useCallback(async () => {
    const canvas = document.createElement('canvas');
    const w = 1080;
    const h = 1080;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, w, h);

    // Image takes 72% of card
    const imgH = h * 0.72;
    const textTop = imgH;
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = walk.photos?.[0] || ''; });
      const scale = Math.max(w / img.width, imgH / img.height);
      ctx.drawImage(img, (w - img.width * scale) / 2, (imgH - img.height * scale) / 2, img.width * scale, img.height * scale);
      // Brightness boost
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fillRect(0, 0, w, imgH);
      // Subtle gradient at bottom edge only
      const grad = ctx.createLinearGradient(0, imgH * 0.78, 0, imgH);
      grad.addColorStop(0, 'rgba(15,23,42,0)');
      grad.addColorStop(1, 'rgba(15,23,42,0.6)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, imgH * 0.78, w, imgH * 0.22);
    } catch {}

    // Category badge overlaid on image (upper left)
    if (category) {
      ctx.font = 'bold 22px -apple-system, system-ui, sans-serif';
      const badgeW = ctx.measureText(category).width + 28;
      ctx.fillStyle = '#FF2D9B';
      roundRect(ctx, 36, 28, badgeW, 32, 16);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.fillText(category, 50, 50);
    }

    // Dark text area
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, textTop, w, h - textTop);

    // Title — generous top padding for breathing room
    let y = textTop + 52;
    ctx.font = 'bold 42px -apple-system, system-ui, sans-serif';
    const titleLines = wrapText(ctx, walk.title || '', w - 100).slice(0, 2);
    if (wrapText(ctx, walk.title || '', w - 100).length > 2) {
      titleLines[1] = titleLines[1].replace(/\s+\S*$/, '...');
    }
    ctx.fillStyle = '#ffffff';
    titleLines.forEach(line => { ctx.fillText(line, 48, y); y += 50; });

    // Stats
    y += 14;
    ctx.font = '24px -apple-system, system-ui, sans-serif';
    ctx.fillStyle = '#c8d1de';
    ctx.fillText([walk.distance, walk.duration].filter(Boolean).join('  ·  '), 48, y);

    // Tagline (centered, above accent line)
    ctx.font = '20px -apple-system, system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    const tagline = 'The guide to walking your city';
    ctx.fillText(tagline, (w - ctx.measureText(tagline).width) / 2, h - 80);

    // Accent line
    ctx.fillStyle = '#A855F7';
    ctx.fillRect(0, h - 62, w, 3);

    // Branding (neon pink)
    ctx.font = 'bold 24px -apple-system, system-ui, sans-serif';
    ctx.fillStyle = '#FF2D9B';
    ctx.fillText('WANDERLOOP', 48, h - 32);
    ctx.font = '20px -apple-system, system-ui, sans-serif';
    ctx.fillText('wanderloop.app', w - 210, h - 32);

    // Download with proper filename
    const link = document.createElement('a');
    link.download = `wanderloop-${walk.slug || 'walk'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, [walk, category, neighborhood]);

  if (!isOpen || !walk) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Share this walk</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Share card preview */}
        <div className="px-5 pt-4">
          <div
            ref={cardRef}
            className="rounded-lg overflow-hidden bg-slate-900 text-white aspect-square shadow-[0_0_12px_rgba(168,85,247,0.15)]"
          >
            {/* Hero image — 72% height with badge overlay */}
            {walk.photos?.[0] && (
              <div className="h-[72%] overflow-hidden relative">
                <img
                  src={walk.photos[0]}
                  alt={walk.title}
                  className="w-full h-full object-cover brightness-110"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 78%, rgba(15,23,42,0.6) 100%)' }} />
                {/* Category badge on image */}
                {category && (
                  <span className="absolute top-2 left-2 text-[9px] font-bold text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FF2D9B' }}>
                    {category}
                  </span>
                )}
              </div>
            )}

            {/* Compact text section — 28% */}
            <div className="px-3 pb-1.5 pt-2 relative flex flex-col justify-between flex-1">
              <div>
                <h4 className="font-bold text-[13px] leading-tight line-clamp-2">{walk.title}</h4>
                <p className="text-slate-300 text-[9px] mt-0.5">
                  {[walk.distance, walk.duration].filter(Boolean).join(' · ')}
                </p>
              </div>

              <div>
                <p className="text-white/35 text-[8px] text-center mb-1.5">The guide to walking your city</p>
                <div className="border-t border-purple-400/30 pt-1 flex items-center justify-between">
                  <span className="font-bold text-[10px] tracking-wider" style={{ color: '#FF2D9B' }}>WANDERLOOP</span>
                  <span className="text-[9px]" style={{ color: '#FF2D9B' }}>wanderloop.app</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={copyLink}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
            >
              <Link2 className="h-4 w-4" />
              Copy Link
            </button>
            <button
              onClick={downloadCard}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              Save Image
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${walkUrl}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(walkUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
            >
              <span className="font-bold">𝕏</span>
              Post on X
            </a>
            <a
              href={`sms:?body=${encodeURIComponent(smsText)}`}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              SMS
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Email
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper: wrap text to fit within maxWidth
function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// Helper: draw rounded rectangle
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export default ShareModal;
